import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, TextField, Button, Stack, Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel, CircularProgress, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { getProductDetail, createProduct, updateProduct } from '../../api/endpoints/shop';
import { getCategories } from '../../api/endpoints/categories';
import { uploadFile } from '../../api/endpoints/media';
import CategoryIcon from '@mui/icons-material/Category';
import CategoryDialog from '../blog/CategoryDialog';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState({
    name: '', description: '', shortDescription: '', category: '', price: '',
    comparePrice: '', stock: '0', weight: '', dimensions: '', material: '', isActive: true, isFeatured: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (isEdit) fetchProduct();
  }, [id]);

  const fetchCategories = async () => {
    try {
      const res = await getCategories({ type: 'product' });
      setCategories(res.data.data || []);
    } catch {
      // ignore
    }
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res = await getProductDetail(id);
      const product = res.data.data;
      setForm({
        name: product.name || '', description: product.description || '', shortDescription: product.shortDescription || '',
        category: product.category?._id || product.category || '', price: product.price || '', comparePrice: product.comparePrice || '',
        stock: product.stock ?? '', weight: product.weight || '', dimensions: product.dimensions || '',
        material: product.material || '', isActive: product.isActive ?? true, isFeatured: product.isFeatured ?? false,
      });
      const existingImage = product.images?.[0] || '';
      if (existingImage) {
        setImageUrl(existingImage);
        setImagePreview(existingImage);
      }
    } catch {
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const res = await uploadFile(file);
      const url = res.data.data.url;
      setImageUrl(url);
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setImageUploading(false);
    }
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setImageUrl('');
    setImagePreview(null);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) {
      toast.error('Name and price are required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        price: Number(form.price),
        comparePrice: Number(form.comparePrice) || 0,
        stock: Number(form.stock) || 0,
      };
      if (!payload.category) delete payload.category;
      if (imageUrl) payload.images = [imageUrl];
      if (isEdit) {
        await updateProduct(id, payload);
        toast.success('Product updated');
      } else {
        await createProduct(payload);
        toast.success('Product created');
      }
      navigate('/shop/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/shop/products')} sx={{ mb: 2 }}>Back</Button>
      <Typography variant="h5" fontWeight={700} gutterBottom>{isEdit ? 'Edit Product' : 'Add Product'}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <TextField label="Product Name" value={form.name} onChange={handleChange('name')} fullWidth required />
                <TextField label="Short Description" value={form.shortDescription} onChange={handleChange('shortDescription')} fullWidth />
                <TextField label="Description" value={form.description} onChange={handleChange('description')} fullWidth multiline rows={4} />
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControl fullWidth size="small"><InputLabel>Category</InputLabel>
                      <Select value={form.category} label="Category" onChange={handleChange('category')}>
                        <MenuItem value="">None</MenuItem>
                        {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}><TextField label="Price (₹)" type="number" value={form.price} onChange={handleChange('price')} fullWidth required /></Grid>
                  <Grid item xs={4}><TextField label="Compare Price" type="number" value={form.comparePrice} onChange={handleChange('comparePrice')} fullWidth /></Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={3}><TextField label="Stock" type="number" value={form.stock} onChange={handleChange('stock')} fullWidth /></Grid>
                  <Grid item xs={3}><TextField label="Weight" value={form.weight} onChange={handleChange('weight')} fullWidth /></Grid>
                  <Grid item xs={3}><TextField label="Dimensions" value={form.dimensions} onChange={handleChange('dimensions')} fullWidth placeholder="e.g. 10x8x2 cm" /></Grid>
                  <Grid item xs={3}><TextField label="Material" value={form.material} onChange={handleChange('material')} fullWidth /></Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Button variant="outlined" startIcon={<CategoryIcon />} onClick={() => setCatDialogOpen(true)} fullWidth>
                      Manage Categories
                    </Button>
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={3}>
                  <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
                  <FormControlLabel control={<Switch checked={form.isFeatured} onChange={(e) => setForm((p) => ({ ...p, isFeatured: e.target.checked }))} />} label="Featured" />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="subtitle2" fontWeight={600}>Product Image</Typography>
                <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />} fullWidth disabled={imageUploading}>
                  {imageUploading ? 'Uploading...' : 'Upload Image'}
                  <input type="file" hidden accept="image/*" onChange={handleUploadImage} />
                </Button>
                {(imagePreview || imageUrl) && (
                  <Box sx={{ position: 'relative', display: 'inline-block', alignSelf: 'center' }}>
                    <img src={imagePreview || imageUrl} alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, objectFit: 'cover' }}
                      onError={(e) => { if (!imagePreview) e.target.style.display = 'none'; }}
                    />
                    <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }} onClick={handleRemoveImage}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Button variant="contained" size="large" fullWidth onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update Product' : 'Create Product')}
          </Button>
        </Grid>
      </Grid>
      <CategoryDialog
        open={catDialogOpen}
        onClose={() => setCatDialogOpen(false)}
        type="product"
        onSaved={fetchCategories}
      />
    </Box>
  );
};

export default ProductForm;
